import { cn } from '@/shared/utils/cn';

export interface CheckboxProps {
  /** Estado del checkbox (marcado o no) */
  checked: boolean;
  /** Estado indeterminado (parcialmente seleccionado) */
  indeterminate?: boolean;
  /** Handler cuando cambia el estado */
  onChange: (checked: boolean) => void;
  /** Si el checkbox está deshabilitado */
  disabled?: boolean;
  /** Si el checkbox tiene estado de error */
  error?: boolean;
  /** Clases CSS adicionales para el input */
  className?: string;
  /** Hace el checkbox redondo en lugar de cuadrado */
  round?: boolean;
  /** Tamaño del checkbox */
  size?: 'sm' | 'md';
  /** Nombre del input para formularios */
  name?: string;
  /** ID del input */
  id?: string;
  /** Texto del label asociado al checkbox */
  label?: string;
  /** Texto de soporte debajo del label */
  supportingText?: string;
  /** Clases CSS adicionales para el contenedor del label */
  classNameLabel?: string;
}

/**
 * Checkbox - Componente de checkbox con soporte para estados indeterminados
 *
 * Componente de checkbox que soporta estados marcados, no marcados e indeterminados.
 * Incluye soporte para labels, texto de soporte y diferentes tamaños.
 *
 * @example
 * ```tsx
 * // Checkbox simple
 * <Checkbox
 *   checked={isChecked}
 *   onChange={(checked) => setIsChecked(checked)}
 * />
 *
 * // Checkbox con label
 * <Checkbox
 *   checked={isChecked}
 *   onChange={(checked) => setIsChecked(checked)}
 *   label="Acepto los términos"
 * />
 * ```
 *
 * @see {@link CheckboxProps} para la lista completa de props
 */
export const Checkbox = ({
  checked,
  indeterminate = false,
  onChange,
  disabled = false,
  error = false,
  className,
  round = false,
  size = 'md',
  name,
  id,
  label,
  supportingText,
  classNameLabel,
}: CheckboxProps) => {
  const isActive = checked || indeterminate;

  // Figma: space_16 = 16px (no existe space_14, se usa valor arbitrario)
  const sizeClasses = {
    sm: 'ui-h-[14px] ui-w-[14px]',
    md: 'ui-h-16 ui-w-16',
  };

  // Figma: check icon 10w × 12h
  const checkmarkSizeClasses = {
    sm: 'ui-w-[8px] ui-h-[10px]',
    md: 'ui-w-[10px] ui-h-[12px]',
  };

  // Figma: minus icon 10w × 12h
  const indeterminateSizeClasses = {
    sm: 'ui-w-[8px] ui-h-[10px]',
    md: 'ui-w-[10px] ui-h-[12px]',
  };

  const inputClasses = cn(
    'ui-relative ui-border-1',
    sizeClasses[size],
    'ui-appearance-none ui-cursor-pointer',
    'ui-transition-colors',
    // Figma: radius/surface/forms = 2px
    round ? 'ui-rounded-full' : 'ui-rounded-surface-forms',

    // ── Default (unchecked, sin error) ──
    !isActive && !disabled && !error && 'ui-bg-fill ui-border-default',
    // ── Checked / Indeterminate (sin error) ──
    // Figma: bg brand, SIN borde visible (transparente)
    isActive && !disabled && !error && 'ui-bg-fill-brand ui-border-transparent',

    // ── Error (unchecked) ──
    // Figma: border/danger = #ef4444
    !isActive && !disabled && error && 'ui-bg-fill ui-border-danger',
    // ── Error (checked / indeterminate) ──
    isActive && !disabled && error && 'ui-bg-fill-brand ui-border-danger',

    // ── Disabled (unchecked) ──
    disabled && !isActive && 'ui-bg-fill-disabled ui-border-disabled ui-cursor-not-allowed',
    // ── Disabled (checked / indeterminate) ──
    // Figma: bg/fill/brand/disabled = #b8c5ee
    disabled && isActive && 'ui-bg-fill-brand-disabled ui-border-transparent ui-cursor-not-allowed',

    // ── Hover ──
    // Figma: border/hover = #85888c (solo unchecked)
    !disabled && !isActive && !error && 'hover:ui-border-hover',
    // Figma: bg/fill/brand/hover = #3251b1 (checked)
    !disabled && isActive && !error && 'hover:ui-bg-fill-brand-hover',

    // ── Focus ──
    // Figma: border/focus = #627dd0 (siempre, 1px)
    !disabled && 'focus:ui-border-focus focus:ui-outline-none',

    className
  );

  const hasLabel = label || supportingText;

  const checkboxBox = (
    <div className="ui-imj ui-relative ui-inline-flex ui-flex-shrink-0 ui-items-center ui-justify-center">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        name={name}
        id={id}
        ref={(el) => {
          if (el) el.indeterminate = indeterminate;
        }}
        className={inputClasses}
        style={{ appearance: 'none', WebkitAppearance: 'none' }}
      />
      {/* Checkmark SVG — Figma: 10w × 12h, color icon/on-color */}
      {checked && !indeterminate && (
        <svg
          className={cn(
            'ui-pointer-events-none ui-absolute ui-text-on-color',
            checkmarkSizeClasses[size]
          )}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={3}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      )}
      {/* Indeterminate minus SVG — Figma: minus-solid, 10w × 12h */}
      {indeterminate && (
        <svg
          className={cn(
            'ui-pointer-events-none ui-absolute ui-text-on-color',
            indeterminateSizeClasses[size]
          )}
          fill="currentColor"
          viewBox="0 0 10 12"
        >
          <rect x="1" y="5" width="8" height="2" rx="0.5" />
        </svg>
      )}
    </div>
  );

  // Sin label: render solo el checkbox box
  if (!hasLabel) {
    return checkboxBox;
  }

  // Con label: estructura Figma _base (flex gap-8 items-center)
  return (
    <div className="ui-imj ui-flex ui-items-start">
      <div className={cn('ui-flex ui-items-center ui-gap-8', classNameLabel)}>
        {checkboxBox}
        {/* Description — Figma: flex-col gap-4 */}
        <div className="ui-flex ui-flex-col ui-gap-4">
          {label && (
            <label
              htmlFor={id}
              className={cn(
                // Figma: type/label/md/Bold — size: 14px, lh: 20.5px, tracking: 0.5px, weight: 700
                'ui-text-label-md-bold ui-font-label-md-bold ui-leading-label-md-bold ui-tracking-label-md',
                'ui-m-0 ui-cursor-pointer ui-select-none',
                // Figma: color/text/primary o color/text/disabled
                disabled ? 'ui-cursor-not-allowed ui-text-disabled' : 'ui-text-primary'
              )}
            >
              {label}
            </label>
          )}
          {supportingText && (
            <span
              className={cn(
                // Figma: type/body/xs/Regular — size: 12px, lh: 17.5px, tracking: 0.5px, weight: 400
                'ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs',
                'ui-m-0',
                // Figma: color/text/tertiary o color/text/disabled
                disabled ? 'ui-text-disabled' : 'ui-text-tertiary'
              )}
            >
              {supportingText}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
